import React from 'react'
import Helmet from 'react-helmet'
import useSiteMetadata from '@helpers/useSiteMetadata'
import {
  jsonLdScript,
  articleSchema,
  breadcrumbSchema,
  organizationSchema,
  webSiteSchema
} from './Seo.schema'

const Seo = ({
  title,
  seoTitle,
  seoDescription,
  description,
  excerpt,
  meta,
  keywords,
  author,
  category,
  date,
  datePublishedIso,
  timeToRead,
  children,
  thumbnail,
  siteUrl,
  pagePath,
  includeSiteSchema,
}) => {

  const site = useSiteMetadata()

  // The page's own path, so the URL does not depend on the render pass or on
  // the address a visitor used. Falls back to siteUrl for callers that pass a
  // complete URL there, and is undefined when a page provides neither.
  const canonicalUrl =
    siteUrl && pagePath
      ? `${siteUrl}/${pagePath.replace(/^\/+/, '')}`
      : siteUrl

  const social = (author && author.social) || site.social || []
  const twitter =
    social.find((s) => s.name && s.name.toLowerCase() === 'twitter') || {}

  description = seoDescription || excerpt || description || site.description

  thumbnail = thumbnail && thumbnail.hero && thumbnail.hero.src
  const thumbnailUrl =
    thumbnail &&
    (thumbnail.startsWith('//')
      ? 'https:'+thumbnail
      : siteUrl && `${siteUrl}${thumbnail}`)

  /**
   * Meta Tags
   */

  const metaTags = [
    { itemprop: 'name', content: seoTitle || title || site.title},
    { itemprop: 'description', content: description },
    { name: 'description', content: description },

    { property: 'og:title', content: title || site.title },
    { property: 'og:description', content: description },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: site.name },
    { property: 'og:image', content: thumbnailUrl },

    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:site', content: site.name },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:creator', content: twitter.url },
    { name: 'twitter:image', content: thumbnailUrl },
  ]

  if (keywords && keywords.length > 0) {
    metaTags.push({ name: 'keywords', content: keywords.join(', ') })
  }

  if (date) {
    metaTags.push({ name: 'article:published_time', content: date })
  }

  if (timeToRead) {
    metaTags.push({ name: 'twitter:label1', value: 'Reading time' })
    metaTags.push({
      name: 'twitter:data1',
      value: `${timeToRead} min read`,
    })
  }

  if (meta) {
    metaTags.push(meta)
  }

  /**
   * Structured Data (JSON-LD)
   */

  const schemas = [
    title &&
      author &&
      articleSchema({
        title,
        thumbnailUrl,
        datePublishedIso,
        canonicalUrl,
        author,
        site,
        siteUrl,
      }),
    title && category && breadcrumbSchema({ category, site, siteUrl }),
    includeSiteSchema && organizationSchema({ site, siteUrl }),
    includeSiteSchema && webSiteSchema({ site, siteUrl }),
  ]

  const scripts = schemas.filter(Boolean).map(jsonLdScript)

  return (
    <Helmet
      htmlAttributes={{
        lang: 'fr',
      }}
      title={seoTitle || title}
      titleTemplate={`%s`}
      meta={metaTags}
      script={scripts}
      link={canonicalUrl ? [{ href: canonicalUrl, rel: 'canonical' }] : []}
    >
      {children}
    </Helmet>
  )
}

export default Seo
